import { Badge } from "@/components/shadcn/ui/badge";
import { cn } from "@/components/shadcn/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/shadcn/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/shadcn/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/ui/tooltip";
import createPersistedState from "@/src/createPersistedState";
import { useSegmentIdentify } from "@/src/segment";
import { EnvData, Tenant, UserData } from "@/src/types";
import { useTenantAction } from "@/src/useAction";
import useEnv from "@/src/useEnv";
import useTenant from "@/src/useTenant";
import useTokens from "@/src/useTokens";
import useUser from "@/src/useUser";
import { useFlag } from "@/src/useFlag";
import {
  Banknote,
  Bell,
  Bot,
  Bug,
  Building2,
  Calculator,
  Check,
  ChevronsUpDown,
  Database,
  HelpCircle,
  Home,
  Layout,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Pencil,
  PlusCircle,
  Send,
  Settings,
  TextSearch,
  Users,
  Hammer,
  Shield,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  AGENT_LANDING_PATH,
  APPS_LANDING_PATH,
  Product,
  useProductMode,
  useProductModeGuard,
} from "oso-for-agents/hooks/useAgentMode";
import {
  APERTURE_ENVIRONMENT_NAME,
  APERTURE_WELCOME_COOKIE,
} from "@/oso-for-agents/constants";
import { useAlertNotifications } from "oso-for-agents/hooks/useAlertNotifications";
import { formatCount } from "oso-for-agents/util";
import { useApertureAutoSwitch } from "@/src/useApertureAutoSwitch";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import LoadingPage from "./LoadingPage";

const useCollapsed = createPersistedState<boolean>("sidebar_collapsed");

// Generate a stable, distinct HSL color for a given environment name.
// - Same name -> same color
// - Different names -> different colors (best-effort)
function getColorForEnvName(name: string): string {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) + hash + name.charCodeAt(i);
    hash |= 0;
  }
  const hue = (hash >>> 0) % 360;
  const saturation = 65;
  const lightness = 45;
  return `hsl(${hue}deg ${saturation}% ${lightness}%)`;
}

// Should only be rendered if the user has access to multiple products (i.e. both Apps and Agents)
// and needs to be able to switch between them.
function ProductSwitcher({
  product,
  onProductChange,
}: {
  product: Product;
  onProductChange: (p: Product) => void;
}) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              tooltip={
                collapsed
                  ? `Oso for ${product === "apps" ? "Apps" : "Agents"}`
                  : undefined
              }
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                {product === "apps" ? (
                  <Layout className="size-4" />
                ) : (
                  <Bot className="size-4" />
                )}
              </div>
              <div className="flex flex-col gap-0.5 leading-none">
                <span className="font-semibold">Oso</span>
                <span className="text-xs text-muted-foreground">
                  for {product === "apps" ? "Apps" : "Agents"}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
            align="start"
          >
            <DropdownMenuItem onClick={() => onProductChange("apps")}>
              <Layout className="mr-2 size-4" />
              <span>Oso for Apps</span>
              {product === "apps" && <Check className="ml-auto size-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onProductChange("agents")}>
              <Bot className="mr-2 size-4" />
              <span>Oso for Agents</span>
              {product === "agents" && <Check className="ml-auto size-4" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// Enables switching between environments easily in the sidebar footer.
function EnvironmentSwitcher({
  selectedEnv,
  environments,
  onEnvironmentChange,
}: {
  selectedEnv: EnvData | null;
  environments: EnvData[];
  onEnvironmentChange: (envName: string) => void;
}) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  if (!selectedEnv || environments.length === 0) {
    return null;
  }

  return (
    <SidebarMenu className="items-center">
      <SidebarMenuItem className="w-auto justify-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="h-auto p-0 items-center justify-center mx-auto focus:ring-0 focus-visible:ring-0"
              tooltip={collapsed ? selectedEnv.name : undefined}
            >
              {collapsed ? (
                <div className="relative">
                  <span
                    className="block size-3 rounded-full relative z-10"
                    style={{
                      backgroundColor: getColorForEnvName(selectedEnv.name),
                    }}
                  />
                </div>
              ) : (
                <Badge
                  variant="outline"
                  className="gap-1.5 rounded-full h-9 px-3 py-0 text-sm font-medium border-none"
                >
                  <div className="relative mr-1 shrink-0 translate-y-px">
                    <span
                      className="block size-3 rounded-full relative z-10"
                      style={{
                        backgroundColor: getColorForEnvName(selectedEnv.name),
                      }}
                    />
                  </div>
                  <span className="overflow-x-hidden overflow-y-visible text-ellipsis whitespace-nowrap">
                    {selectedEnv.name}
                  </span>
                  <ChevronsUpDown className="ml-1 size-4 opacity-60" />
                </Badge>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-44"
            align={collapsed ? "start" : "center"}
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Environments
            </DropdownMenuLabel>
            {environments.map((env) => (
              <DropdownMenuItem
                key={env.pub_id}
                onClick={() => onEnvironmentChange(env.name)}
                className="items-center"
              >
                <div
                  className="mr-2 size-3 rounded-full shrink-0 translate-y-px"
                  style={{ backgroundColor: getColorForEnvName(env.name) }}
                />
                <span>{env.name}</span>
                {selectedEnv.pub_id === env.pub_id && (
                  <Check className="ml-auto size-4" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// Enables switching between tenants that the user has access to in the sidebar footer.
function TenantSwitcher({
  tenant,
  tenants,
  onTenantChange,
  user,
  onLogout,
}: {
  tenant: Tenant | null;
  tenants: Tenant[];
  onTenantChange: (t: Tenant) => void;
  user: UserData | null;
  onLogout: () => void;
}) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  // Note: titleText below is the canonical display for the tenant in the trigger.
  const invitationCount = user?.pending_invitations?.length ?? 0;
  const tenantKindLabel = tenant
    ? tenant.kind === "production"
      ? "Business"
      : "Developer"
    : null;
  // Consistent title rule: sandbox -> owner name (fallback to tenant name), production -> tenant name
  const titleText =
    tenant?.kind === "sandbox"
      ? tenant?.sandbox_owner_name || tenant?.name || "Unknown"
      : tenant?.name || "Unknown";
  const subtitleText = tenantKindLabel ? `${tenantKindLabel} Tier` : "";
  // Tooltip when collapsed
  const collapsedTooltip = tenant
    ? `${titleText}${tenantKindLabel ? ` (${tenantKindLabel} Tier)` : ""}`
    : undefined;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              tooltip={collapsed ? collapsedTooltip : undefined}
            >
              <div
                className="flex aspect-square size-8 items-center justify-center rounded-lg text-xs font-medium"
                style={{
                  backgroundColor: getColorForEnvName(titleText ?? ""),
                  color: "white",
                }}
              >
                {titleText?.charAt(0).toUpperCase() ?? "O"}
              </div>
              <div className="grid flex-1 text-left text-sm gap-0.5">
                <span className="truncate font-semibold">{titleText}</span>
                <div className="min-w-0 flex items-center gap-1">
                  {subtitleText && (
                    <span className="truncate text-xs text-muted-foreground">
                      {subtitleText}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-1">
                {invitationCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="px-1 h-5 min-w-[1.25rem] text-[10px] font-semibold justify-center"
                  >
                    {invitationCount}
                  </Badge>
                )}
                <ChevronsUpDown className="size-4" />
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
            side="top"
            align="start"
            sideOffset={4}
          >
            {tenant?.kind !== "tailnet" && tenants.length > 1 && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Building2 className="mr-2 size-4" />
                    <span>Switch organization…</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    sideOffset={4}
                    alignOffset={-4}
                    className="pt-0"
                  >
                    {(() => {
                      const businessTenants = tenants.filter(
                        (t) => t.kind === "production"
                      );
                      const developerTenants = tenants.filter(
                        (t) => t.kind === "sandbox"
                      );
                      const getTenantTitle = (t: Tenant) =>
                        t.kind === "sandbox"
                          ? t.sandbox_owner_name || t.name
                          : t.name;

                      return (
                        <>
                          {businessTenants.length > 0 && (
                            <>
                              <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
                                Business Tier
                              </DropdownMenuLabel>
                              {businessTenants.map((t) => {
                                const displayName = getTenantTitle(t);
                                const avatar = (displayName || "O")
                                  .charAt(0)
                                  .toUpperCase();
                                return (
                                  <DropdownMenuItem
                                    key={`biz-${t.id}`}
                                    onClick={() => onTenantChange(t)}
                                  >
                                    <div
                                      className="flex size-6 items-center justify-center rounded bg-muted text-xs font-medium"
                                      style={{
                                        backgroundColor:
                                          getColorForEnvName(displayName),
                                        color: "white",
                                      }}
                                    >
                                      {avatar}
                                    </div>
                                    <span className="ml-2">{displayName}</span>
                                    {tenant?.id === t.id && (
                                      <Check className="ml-auto size-4" />
                                    )}
                                  </DropdownMenuItem>
                                );
                              })}
                            </>
                          )}

                          {developerTenants.length > 0 && (
                            <>
                              <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground flex items-center gap-1">
                                <span>Developer Tier</span>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <HelpCircle className="size-3 opacity-70" />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    align="center"
                                    className="max-w-[220px] text-xs"
                                  >
                                    <p>
                                      For experimentation only, with limited
                                      features and performance. <br />
                                      <br />
                                      <a
                                        href="https://www.osohq.com/meet-the-oso-founder-ceo?utm_source=ui&utm_medium=ui&utm_campaign=14/11"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline"
                                      >
                                        Contact us
                                      </a>{" "}
                                      to upgrade to Business Tier for production
                                      use.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </DropdownMenuLabel>
                              {developerTenants.map((t) => {
                                const displayName = getTenantTitle(t);
                                const nameWithSuffix =
                                  t.sandbox_owned_by_current_user
                                    ? `${displayName} (You)`
                                    : displayName;
                                const avatar = (displayName || "O")
                                  .charAt(0)
                                  .toUpperCase();
                                return (
                                  <DropdownMenuItem
                                    key={`dev-${t.id}`}
                                    onClick={() => onTenantChange(t)}
                                  >
                                    <div
                                      className="flex size-6 items-center justify-center rounded bg-muted text-xs font-medium"
                                      style={{
                                        backgroundColor:
                                          getColorForEnvName(displayName),
                                        color: "white",
                                      }}
                                    >
                                      {avatar}
                                    </div>
                                    <span className="ml-2">
                                      {nameWithSuffix}
                                    </span>
                                    {tenant?.id === t.id && (
                                      <Check className="ml-auto size-4" />
                                    )}
                                  </DropdownMenuItem>
                                );
                              })}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Actions */}
            <Link href="/settings/" legacyBehavior passHref>
              <DropdownMenuItem asChild>
                <div>
                  <Settings className="mr-2 size-4" />
                  <span>Organization settings</span>
                </div>
              </DropdownMenuItem>
            </Link>
            {tenant?.kind !== "tailnet" && (
              <Link href="/user-settings/" legacyBehavior passHref>
                <DropdownMenuItem asChild>
                  <div className="flex items-center gap-2 w-full">
                    <Send className="mr-2 size-4" />
                    <span>Invitations</span>
                    {invitationCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="ml-auto px-1.5 h-5 min-w-[1.25rem] text-[10px] font-semibold justify-center"
                      >
                        {invitationCount}
                      </Badge>
                    )}
                  </div>
                </DropdownMenuItem>
              </Link>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOut className="mr-2 size-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// Reusable callout component for sidebar footer messages
type SidebarCalloutProps = {
  title: string;
  children?: React.ReactNode;
  centered?: boolean;
} & (
  | { href: string; external?: boolean; onClick?: never }
  | { onClick: () => void; href?: never; external?: never }
);

function SidebarCallout({
  href,
  title,
  children,
  external,
  centered,
  onClick,
}: SidebarCalloutProps) {
  const content = (
    <div
      className={`hover:scale-105 transition-all rounded text-sm w-48 py-2 px-4 cursor-pointer${
        centered ? " text-center" : ""
      }`}
      style={{
        background:
          "radial-gradient(circle,rgba(230, 215, 242, 1) 0%, rgba(221, 211, 237, 1) 100%)",
      }}
    >
      <div className={children ? "mb-1" : undefined}>
        <div className={children ? "font-semibold mb-1" : "font-semibold"}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );

  return (
    <div className="self-center px-2 py-2">
      {onClick ? (
        <button
          onClick={onClick}
          type="button"
          className={centered ? "text-center" : "text-left"}
        >
          {content}
        </button>
      ) : external ? (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      ) : (
        <Link href={href}>{content}</Link>
      )}
    </div>
  );
}

// Callout for the "Agents" product to get more inbound.
function LeastPrivilegeCallout() {
  return (
    <SidebarCallout
      href="https://www.osohq.com/meet-the-oso-founder-ceo?utm_source=ui&utm_medium=ui&utm_campaign=14/11"
      title="Working on AI agents in production?"
      external
    >
      Schedule a meeting to see what we&apos;re building.
    </SidebarCallout>
  );
}

interface SidebarItem {
  title: string;
  url: string;
  icon: React.ComponentType;
  badge?: number;
}

interface SidebarGroup {
  label?: string;
  items: SidebarItem[];
}

export function AppSidebar() {
  useSegmentIdentify();
  const router = useRouter();
  const {
    product,
    setProduct,
    showAgentsProductOnly,
    showBothAppsAndAgentsProduct,
  } = useProductMode();
  const { maybeAutoSwitchToAgents } = useApertureAutoSwitch();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Real tenant data
  const { tenant, tenants, setTenant } = useTenant();

  // Real environment data
  const { selectedEnv, setSelectedEnvName } = useEnv();
  const environments = tenant?.environments || [];

  // Hide contents until tenant arrives so flag-gated elements (Models tab,
  // product label, footer callouts) resolve while invisible instead of
  // popping in and rejiggering the layout.
  const fadeClass = cn(
    "transition-opacity duration-200",
    tenant ? "opacity-100" : "opacity-0"
  );

  // Real user data
  const { user, logout } = useUser();

  // Alert notifications (only active for agents product)
  const { openCount: alertCount } = useAlertNotifications(product === "agents");
  const policyBuilderEnabled = useFlag("agent-policy-builder-enabled");

  // Check for API tokens in Aperture environment (only for agents product)
  // Don't show callout if welcome cookie exists (dashboard modal will handle it)
  const isApertureEnv = selectedEnv?.name === APERTURE_ENVIRONMENT_NAME;
  const shouldCheckTokens = isApertureEnv && product === "agents";
  const { data: tokens } = useTokens(
    shouldCheckTokens ? selectedEnv?.pub_id : undefined
  );
  // Start true to avoid flash; re-check on every render to detect cookie deletion
  const [hasWelcomeCookie, setHasWelcomeCookie] = useState(true);
  useEffect(() => {
    const exists = document.cookie
      .split(";")
      .some((c) => c.trim().startsWith(`${APERTURE_WELCOME_COOKIE}=`));
    setHasWelcomeCookie(exists);
  });
  const showNoTokensMessage =
    shouldCheckTokens && tokens && tokens.length === 0 && !hasWelcomeCookie;

  // Get navigation items based on selected product
  const viewBillingAction = useTenantAction("view_billing");
  const showBilling = tenant?.kind === "production" && viewBillingAction;

  const appsLinks = useMemo(
    () => [
      { title: "Dashboard", url: "/dashboard", icon: Home },
      { title: "Rules", url: "/rules-editor", icon: Pencil },
      { title: "Data", url: "/facts", icon: Database },
      { title: "Explain", url: "/explain", icon: Bug },
      { title: "Logs", url: "/logs", icon: TextSearch },
      { title: "Install", url: "/install", icon: PlusCircle },
      { title: "Usage", url: "/usage", icon: Calculator },
      ...(showBilling
        ? [{ title: "Billing", url: "/billing", icon: Banknote }]
        : []),
    ],
    [showBilling]
  );

  const agentsNavGroups = useMemo(
    (): SidebarGroup[] => [
      {
        items: [
          {
            title: "Alerts",
            url: "/agent-app/alerts",
            icon: Bell,
            badge: alertCount > 0 ? alertCount : undefined,
          },
        ],
      },
      {
        label: "Discover",
        items: [
          { title: "Agents", url: "/agent-app/agents", icon: Bot },
          {
            title: "Tools",
            url: "/agent-app/tools",
            icon: Hammer,
          },
          {
            title: "Users",
            url: "/agent-app/discover/users",
            icon: Users,
          },
        ],
      },
      {
        label: "Monitor",
        items: [
          {
            title: "Dashboard",
            url: AGENT_LANDING_PATH,
            icon: LayoutDashboard,
          },
          {
            title: "Sessions",
            url: "/agent-app/sessions",
            icon: MessageSquare,
          },
        ],
      },
      ...(policyBuilderEnabled
        ? [
            {
              label: "Govern",
              items: [
                {
                  title: "Policies",
                  url: "/agent-app/policies",
                  icon: Shield,
                },
              ],
            },
          ]
        : []),
      {
        label: "Configure",
        items: [
          {
            title: "Connections",
            url: "/agent-app/connections",
            icon: Settings,
          },
        ],
      },
    ],
    [alertCount, policyBuilderEnabled]
  );

  // Check if a nav item is active based on current route
  const isItemActive = (url: string) => {
    const pathname = router.pathname;
    // Exact match for dashboard/home pages
    if (url === "/dashboard" || url === AGENT_LANDING_PATH) {
      return pathname === url;
    }
    // Prefix match for other pages (handles nested routes)
    return pathname.startsWith(url);
  };

  const handleProductChange = (p: Product) => {
    setProduct(p);
    if (p === "apps") {
      router.push(APPS_LANDING_PATH);
    } else {
      router.push(AGENT_LANDING_PATH);
    }
  };

  // Wrapper for environment change that auto-switches to Agents UI
  // when a hybrid user switches to an Aperture env with no API tokens
  const handleEnvironmentChange = (envName: string) => {
    setSelectedEnvName(envName);
    const newEnv = environments.find((e) => e.name === envName);
    if (newEnv) {
      maybeAutoSwitchToAgents(envName, newEnv.pub_id);
    }
  };

  // End impersonation session
  const endImpersonation = useCallback(async () => {
    await fetch("/web/clear-impersonation", { method: "POST" });
    router.reload();
  }, [router]);

  const canSwitchProducts =
    showBothAppsAndAgentsProduct && !showAgentsProductOnly;
  const headerBody = canSwitchProducts ? (
    <ProductSwitcher product={product} onProductChange={handleProductChange} />
  ) : (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          tooltip={
            isCollapsed
              ? `Oso for ${product === "apps" ? "Apps" : "Agents"}`
              : undefined
          }
        >
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-[#34288F]">
            <img
              src="/logo-purple-circle-bear.svg"
              alt="Oso"
              className="h-6 w-auto object-contain rounded-full shadow-xl grow-0"
            />
          </div>
          <div className="flex flex-col gap-0.5 leading-none">
            <span className="font-semibold">Oso</span>
            <span className="text-xs text-muted-foreground">
              for {product === "apps" ? "Apps" : "Agents"}
            </span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );

  return (
    <Sidebar collapsible="icon">
      {/* HEADER: Logo + Product Switcher */}
      <SidebarHeader className={fadeClass}>{headerBody}</SidebarHeader>

      {/* CONTENT: Main navigation */}
      <SidebarContent className={fadeClass}>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {product == "agents"
                ? agentsNavGroups.map((group, groupIndex) => (
                    <SidebarGroup
                      key={group.label ?? groupIndex}
                      className="px-0"
                    >
                      {group.label && (
                        <SidebarGroupLabel className="text-[10px] text-muted-foreground/80 uppercase font-normal h-6 select-none">
                          {group.label}
                        </SidebarGroupLabel>
                      )}
                      <SidebarGroupContent>
                        <SidebarMenu>
                          {group.items.map((item) => (
                            <SidebarMenuItem key={item.title}>
                              <SidebarMenuButton
                                asChild
                                tooltip={item.title}
                                isActive={isItemActive(item.url)}
                                className="cursor-pointer select-none"
                              >
                                <Link href={item.url}>
                                  <item.icon />
                                  <span>{item.title}</span>
                                  {"badge" in item &&
                                    item.badge !== undefined &&
                                    item.badge > 0 && (
                                      <Badge className="ml-auto bg-red-500 hover:bg-red-500 text-white text-[10px] px-1.5 h-5 min-w-[1.25rem] justify-center">
                                        {formatCount(item.badge)}
                                      </Badge>
                                    )}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  ))
                : appsLinks.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        isActive={isItemActive(item.url)}
                        className="cursor-pointer select-none"
                      >
                        <Link href={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* FOOTER: User + Tenant Switcher */}
      <SidebarFooter className={fadeClass}>
        {user?.impersonated_by && !isCollapsed && (
          <SidebarCallout
            title="IMPERSONATING"
            onClick={endImpersonation}
            centered
          >
            <div className="truncate pb-2">{user.login_identifier}</div>
            <div className="text-xs text-muted-foreground">(click to end)</div>
          </SidebarCallout>
        )}
        {product === "apps" &&
          !showAgentsProductOnly &&
          !showBothAppsAndAgentsProduct &&
          !isCollapsed && <LeastPrivilegeCallout />}
        {showNoTokensMessage && !isCollapsed && (
          <SidebarCallout
            href="/settings?api-keys"
            title="Still need to connect to Aperture?"
          >
            Manage API keys here.
          </SidebarCallout>
        )}
        {tenant && (
          <div className="flex justify-center">
            <div className="w-full px-2 flex justify-center">
              <EnvironmentSwitcher
                selectedEnv={selectedEnv}
                environments={environments}
                onEnvironmentChange={handleEnvironmentChange}
              />
            </div>
          </div>
        )}
        <TenantSwitcher
          tenant={tenant}
          tenants={tenants}
          onTenantChange={setTenant}
          user={user}
          onLogout={logout}
        />
      </SidebarFooter>
    </Sidebar>
  );
}

export default function WithAppSidebar({
  children,
}: {
  children: React.ReactNode;
}) {
  // Default to collapsed so the post-mount transition (when the user's
  // stored preference differs from the default) runs collapsed-then-expand
  // rather than expanded-then-snap-closed.
  const [collapsed, setCollapsed] = useCollapsed(true);
  const canViewPage = useProductModeGuard();

  return (
    <SidebarProvider
      defaultOpen={false}
      open={!collapsed}
      onOpenChange={(open) => setCollapsed(!open)}
    >
      <AppSidebar />
      <main className="flex-1 min-w-0">
        <SidebarTrigger />
        {canViewPage ? children : <LoadingPage />}
      </main>
    </SidebarProvider>
  );
}
